import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const statusColors: Record<string, string> = {
  Approved: 'bg-green-100 text-green-800',
  Pending: 'bg-yellow-100 text-yellow-800',
  Rejected: 'bg-red-100 text-red-800',
  'In Progress': 'bg-blue-100 text-blue-800',
};

const RequestList: React.FC = () => {
  const { user } = useAuth();
  const [requests, setRequests] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (!user) return;
    const fetchRequests = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from('service_requests')
        .select(`
          *,
          brands!inner(name)
        `)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });
      if (!error) setRequests(data || []);
      setLoading(false);
    };
    fetchRequests();
  }, [user]);

  // Filter requests based on search term
  const filteredRequests = requests.filter(req => {
    const searchLower = searchTerm.toLowerCase();
    const brandName = req.brands?.name?.toLowerCase() || '';
    const requestType = req.type?.replace(/-/g, ' ').toLowerCase() || '';
    
    return brandName.includes(searchLower) || requestType.includes(searchLower);
  });

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-20 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow">
      <div className="p-6 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h2 className="text-xl font-semibold text-gray-900">Your Requests</h2>
          <input
            type="text"
            placeholder="Search requests..."
            className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>
      
      <div className="p-6">
        {filteredRequests.length === 0 ? (
          <div className="text-center py-8">
            <div className="text-gray-400 mb-4">
              <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No requests yet</h3>
            <p className="text-gray-500">Create your first design request to get started.</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredRequests.map((req) => (
              <Link
                to={`/dashboard/requests/${req.id}`}
                state={{ answers: req.answers }}
                key={req.id}
                className="flex items-center justify-between bg-gray-50 rounded-lg p-4 border hover:bg-gray-100 transition cursor-pointer"
              >
                <div>
                  <div className="font-semibold text-lg text-gray-900">
                    {req.type.replace(/-/g, ' ').replace(/\b\w/g, (c: string) => c.toUpperCase())}
                  </div>
                  <div className="text-gray-500 text-sm">
                    <span className="font-medium">{req.brands?.name || 'Unknown Brand'}</span> • Requested on {new Date(req.created_at).toLocaleDateString()}
                  </div>
                </div>
                <span
                  className={`px-3 py-1 rounded-full text-sm font-semibold ${statusColors[req.status] || 'bg-gray-100 text-gray-800'}`}
                >
                  {req.status || 'Pending'}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestList; 